import { MapPin, Phone, Mail, Instagram, Youtube, Clock } from "lucide-react";
import footerLogo from "@/assets/ew-footer-logo.png";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { WHATSAPP_DIRECT } from "@/lib/whatsapp";

const Footer = () => {
  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="container mx-auto px-4 py-10 sm:py-12 md:py-16">
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-8 sm:gap-10 md:gap-12 mb-8 sm:mb-10 md:mb-12">
          {/* Coluna 1 - Identificação da Empresa */}
          <div className="space-y-3 sm:space-y-4">
            <img 
              src={footerLogo} 
              alt="Logo EW Saúde Ambiental - Dedetização em Pernambuco" 
              width={160}
              height={56}
              className="h-12 sm:h-14 w-auto mb-4 sm:mb-6"
            />
            <div>
              <h3 className="font-bold text-base sm:text-lg mb-2">EW Saúde Ambiental</h3>
              <p className="text-primary-foreground/80 text-xs sm:text-sm leading-relaxed">
                Empresa especializada em controle de pragas urbanas e saúde ambiental.
              </p>
            </div>
            <div className="text-xs sm:text-sm text-primary-foreground/80 space-y-1 pt-2">
              <p><span className="font-semibold text-primary-foreground">CNPJ:</span> 23.454.329/0001-28</p>
            </div>
          </div>
          
          {/* Coluna 2 - Contatos */}
          <div className="space-y-6">
            <div>
              <h3 className="font-bold text-lg mb-4">Contato</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Phone className="w-4 h-4 mt-0.5 flex-shrink-0 text-secondary" />
                  <a 
                    href="tel:+5581993942858" 
                    className="text-sm text-primary-foreground/80 hover:text-secondary transition-[var(--transition-smooth)]"
                  >
                    (81) 9.9394-2858
                  </a>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="w-4 h-4 mt-0.5 flex-shrink-0 text-secondary" />
                  <a 
                    href="mailto:comercialewsaudeambiental@gmail.com" 
                    className="text-sm text-primary-foreground/80 hover:text-secondary transition-[var(--transition-smooth)] break-all"
                  >
                    comercialewsaudeambiental@gmail.com
                  </a>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-secondary" />
                  <div className="text-sm text-primary-foreground/80">
                    <p>Travessa 1º Tiradentes</p>
                    <p>Lagoa do Sapo</p>
                    <p>Feira Nova - PE, 55715-000</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-secondary" />
                Horários
              </h3>
              <ul className="text-sm text-primary-foreground/80 space-y-1.5">
                <li>Segunda a Sexta: <span className="text-primary-foreground">8h às 17h</span></li>
                <li>Sábado: <span className="text-primary-foreground">8h às 12h</span></li>
              </ul>
            </div>
          </div>
          
          {/* Coluna 3 - Links Úteis */}
          <div>
            <h3 className="font-bold text-lg mb-4">Links Úteis</h3>
            <ul className="space-y-2.5">
              <li>
                <Link 
                  to="/" 
                  className="text-sm text-primary-foreground/80 hover:text-secondary transition-[var(--transition-smooth)] inline-block"
                >
                  Página Inicial
                </Link>
              </li>
              <li>
                <Link 
                  to="/sobre-nos" 
                  className="text-sm text-primary-foreground/80 hover:text-secondary transition-[var(--transition-smooth)] inline-block"
                >
                  Sobre Nós
                </Link>
              </li>
              <li>
                <a 
                  href="/#servicos" 
                  className="text-sm text-primary-foreground/80 hover:text-secondary transition-[var(--transition-smooth)] inline-block"
                >
                  Nossos Serviços
                </a>
              </li>
              <li>
                <Link 
                  to="/blog" 
                  className="text-sm text-primary-foreground/80 hover:text-secondary transition-[var(--transition-smooth)] inline-block"
                >
                  Blog e Dicas
                </Link>
              </li>
              <li>
                <a 
                  href="/#contato" 
                  className="text-sm text-primary-foreground/80 hover:text-secondary transition-[var(--transition-smooth)] inline-block"
                >
                  Contato
                </a>
              </li>
              <li>
                <a 
                  href="/#faq" 
                  className="text-sm text-primary-foreground/80 hover:text-secondary transition-[var(--transition-smooth)] inline-block"
                >
                  Perguntas Frequentes
                </a>
              </li>
              <li>
                <Link 
                  to="/trabalhe-conosco" 
                  className="text-sm text-primary-foreground/80 hover:text-secondary transition-[var(--transition-smooth)] inline-block"
                >
                  Trabalhe Conosco
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Coluna 4 - Redes Sociais e CTA */}
          <div className="space-y-5 sm:space-y-6">
            <div>
              <h3 className="font-bold text-base sm:text-lg mb-3 sm:mb-4">Redes Sociais</h3>
              <div className="flex gap-2 sm:gap-3">
                <a 
                  href="https://www.instagram.com/ewsaudeambiental/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-primary-foreground/10 hover:bg-secondary p-3 rounded-full transition-[var(--transition-smooth)] hover:scale-110"
                  aria-label="Instagram da EW Saúde Ambiental"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a 
                  href="https://www.tiktok.com/@ewsaudeambiental?lang=pt-BR" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-primary-foreground/10 hover:bg-secondary p-3 rounded-full transition-[var(--transition-smooth)] hover:scale-110"
                  aria-label="TikTok da EW Saúde Ambiental"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </a>
                <a 
                  href="https://www.youtube.com/@EWSa%C3%BAdeAmbiental" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-primary-foreground/10 hover:bg-secondary p-3 rounded-full transition-[var(--transition-smooth)] hover:scale-110"
                  aria-label="YouTube da EW Saúde Ambiental"
                >
                  <Youtube className="w-5 h-5" />
                </a>
              </div>
            </div>

            <div className="bg-primary-foreground/10 p-4 sm:p-5 rounded-xl border border-primary-foreground/20">
              <p className="text-xs sm:text-sm font-semibold mb-2 sm:mb-3 text-center">
                💬 Agende agora!
              </p>
              <Button
                asChild
                variant="whatsapp"
                className="w-full touch-manipulation"
                size="lg"
              >
                <a 
                  href={WHATSAPP_DIRECT}
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  Falar no WhatsApp
                </a>
              </Button>
            </div>
          </div>
        </div>
        
        {/* Avisos Legais e SEO */}
        <div className="border-t border-primary-foreground/20 pt-6 sm:pt-8 space-y-3 sm:space-y-4">
          <div className="text-center">
            <p className="text-[10px] sm:text-xs text-primary-foreground/70 mb-2 sm:mb-3 max-w-3xl mx-auto px-2">
              Empresa licenciada pela Vigilância Sanitária e autorizada a atuar com produtos registrados na Anvisa.
            </p>
            <p className="text-[10px] sm:text-xs text-primary-foreground/70 leading-relaxed max-w-5xl mx-auto px-2">
              <strong className="text-primary-foreground">Atendemos todo Pernambuco:</strong> Feira Nova, Limoeiro, Carpina, Paudalho, Surubim, Nazaré da Mata, Recife, 
              Caruaru, Olinda, Jaboatão dos Guararapes, Vitória de Santo Antão, Gravatá, Abreu e Lima, 
              Camaragibe, Paulista, Cabo de Santo Agostinho e região metropolitana.
              Especialistas em <strong className="text-primary-foreground">dedetização</strong>, <strong className="text-primary-foreground">descupinização</strong>, <strong className="text-primary-foreground">desratização</strong>, 
              <strong className="text-primary-foreground">desinsetização</strong> e <strong className="text-primary-foreground">sanitização</strong>.
            </p>
          </div>

          <p className="text-center text-[10px] sm:text-xs text-primary-foreground/60 pt-1 sm:pt-2 px-2">
            &copy; {new Date().getFullYear()} EW Saúde Ambiental - Todos os direitos reservados.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
